import { __decorate } from "tslib";
import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import shallowEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import isElement from 'lodash/isElement';
import Align from '../../../es/align';
import { getProPrefixCls } from '../../../es/configure';
import Animate from '../animate';
import ViewComponent from '../core/ViewComponent';
import PopupInner from './PopupInner';
import autobind from '../_util/autobind';
/**
 * 记录ID生成器
 */
const PopupKeyGen = (function* (start) {
    while (true) {
        yield `popup-key-${start++}`;
    }
})(1);
export default class Popup extends ViewComponent {
    constructor() {
        super(...arguments);
        this.contentRendered = false;
        this.popupKey = PopupKeyGen.next().value;
        this.saveRef = align => (this.align = align);
    }
    getOtherProps() {
        const otherProps = omit(super.getOtherProps(), [
            'align',
            'transitionName',
            'getRootDomNode',
            'getPopupContainer',
            'getClassNameFromAlign',
            'getStyleFromAlign',
            'onAlign',
            'onAnimateAppear',
            'onAnimateEnter',
            'onAnimateLeave',
            'onAnimateEnd',
        ]);
        return otherProps;
    }
    componentWillUnmount() {
        const { popupContainer } = this;
        if (popupContainer && popupContainer !== Popup.popupContainer && popupContainer.parentNode) {
            popupContainer.parentNode.removeChild(popupContainer);
        }
    }
    render() {
        const { hidden, align, transitionName, getRootDomNode, children, onAnimateAppear = noop, onAnimateEnter = noop, onAnimateLeave = noop, onAnimateEnd = noop, } = this.props;
        if (!hidden) {
            this.contentRendered = true;
        }
        const container = this.getContainer();
        return container && this.contentRendered ? createPortal(React.createElement(Animate, { component: "", exclusive: true, transitionAppear: true, transitionName: transitionName, hiddenProp: "hidden", onAppear: onAnimateAppear, onEnter: onAnimateEnter, onLeave: onAnimateLeave, onEnd: onAnimateEnd },
            React.createElement(Align, { ref: this.saveRef, key: "align", childrenProps: { hidden: 'hidden' }, align: align, onAlign: this.onAlign, target: getRootDomNode, hidden: hidden, monitorWindowResize: true },
                React.createElement(PopupInner, Object.assign({}, omit(this.getMergedProps(), ['ref'])), children))), container, this.popupKey) : null;
    }
    getContainer() {
        if (typeof window === 'undefined') {
            return undefined;
        }
        const { getPopupContainer, getRootDomNode = noop } = this.props;
        const globalContainer = Popup.popupContainer;
        if (getPopupContainer) {
            const container = this.popupContainer;
            if (container) {
                return container;
            }
        }
        else if (globalContainer) {
            return globalContainer;
        }
        const doc = window.document;
        const popupContainer = doc.createElement('div');
        popupContainer.className = getProPrefixCls('popup-container');
        const root = doc.body;
        if (getPopupContainer) {
            const mountNode = getPopupContainer(getRootDomNode());
            if (mountNode === root) {
                if (globalContainer) {
                    this.popupContainer = globalContainer;
                    return globalContainer;
                }
                Popup.popupContainer = popupContainer;
            }
            (isElement(mountNode) ? mountNode : root).appendChild(popupContainer);
            this.popupContainer = popupContainer;
        }
        else {
            root.appendChild(popupContainer);
            Popup.popupContainer = popupContainer;
        }
        return popupContainer;
    }
    onAlign(source, align, target, translate) {
        const { getClassNameFromAlign = noop, getStyleFromAlign = noop, onAlign = noop } = this.props;
        const currentAlignClassName = getClassNameFromAlign(align);
        if (this.currentAlignClassName !== currentAlignClassName) {
            this.currentAlignClassName = currentAlignClassName;
            source.className = this.getMergedClassNames(currentAlignClassName);
        }
        const currentAlignStyle = getStyleFromAlign(target, align);
        if (!shallowEqual(this.currentAlignStyle, currentAlignStyle)) {
            this.currentAlignStyle = currentAlignStyle;
            Object.assign(source.style, currentAlignStyle);
        }
        onAlign(source, align, target, translate);
    }
    forceAlign() {
        if (this.align) {
            this.align.forceAlign();
        }
    }
}
Popup.displayName = 'Popup';
Popup.propTypes = {
    align: PropTypes.object,
    onAlign: PropTypes.func,
    getRootDomNode: PropTypes.func,
    getPopupContainer: PropTypes.func,
    transitionName: PropTypes.string,
    onAnimateAppear: PropTypes.func,
    onAnimateEnter: PropTypes.func,
    onAnimateLeave: PropTypes.func,
    onAnimateEnd: PropTypes.func,
    getStyleFromAlign: PropTypes.func,
    getClassNameFromAlign: PropTypes.func,
    ...ViewComponent.propTypes,
};
Popup.defaultProps = {
    suffixCls: 'popup',
    transitionName: 'zoom',
};
__decorate([
    autobind
], Popup.prototype, "onAlign", null);
